import { useCallback, useMemo } from "react";

import {
  AppBar,
  Authenticator,
  CircularProgressCenter,
  Drawer,
  FireCMS,
  FireCMSi18nProvider,
  ModeControllerProvider,
  NavigationRoutes,
  Scaffold,
  SideDialogs,
  SnackbarProvider,
  useBuildLocalConfigurationPersistence,
  useBuildModeController,
  useBuildNavigationController,
  useValidateAuthenticator,
} from "@firecms/core";
import {
  FirebaseAuthController,
  FirebaseLoginView,
  FirebaseSignInProvider,
  FirebaseUserWrapper,
  useFirebaseAuthController,
  useFirebaseStorageSource,
  useFirestoreDelegate,
  useInitialiseFirebase,
} from "@firecms/firebase";
import { CenteredView } from "@firecms/ui";
import { doc, getDoc, getFirestore } from "firebase/firestore";

import { firebaseConfig } from "./firebase_config";
import { testimonialsCollection } from "./collections/testimonials";
import { blogPostsCollection } from "./collections/blogPosts";
import { homePageCollection } from "./collections/pages/home";
import { aboutPageCollection } from "./collections/pages/about";
import { contactPageCollection } from "./collections/pages/contact";
import { communityPageCollection } from "./collections/pages/community";
import { servicesPageCollection } from "./collections/pages/services";
import { pageShortcut } from "./views/pageShortcut";

function App() {
  const {
    firebaseApp,
    firebaseConfigLoading,
    configError,
  } = useInitialiseFirebase({ firebaseConfig });

  // Gates the UI to admins. Firestore rules enforce the same on every write.
  const myAuthenticator: Authenticator<FirebaseUserWrapper> = useCallback(
    async ({ user }) => {
      if (!user?.uid || !firebaseApp) return false;
      const db = getFirestore(firebaseApp);
      const snap = await getDoc(doc(db, "admins", user.uid));
      if (!snap.exists()) return false;
      if (snap.get("isAdmin")==true) return true; // in case of undefined or any other fuck ass value
      return false;
    },
    [firebaseApp],
  );

  // Pages first — that is where most editing happens.
  const collections = useMemo(
    () => [
      homePageCollection,
      aboutPageCollection,
      servicesPageCollection,
      contactPageCollection,
      communityPageCollection,
      testimonialsCollection,
      blogPostsCollection,
    ],
    [],
  );

  // Each page is a single document, so the drawer skips the one-row list and
  // opens the edit form directly. One entry per migrated page.
  const views = useMemo(
    () => [
      pageShortcut({
        path: "home",
        name: "Home",
        icon: "Home",
        description: "Every piece of text and every photo on the Home page.",
        collectionId: "page-home",
        entityId: "home",
      }),
      pageShortcut({
        path: "about-me",
        name: "About Me",
        icon: "Person",
        description: "Every piece of text and every photo on the About Me page.",
        collectionId: "page-about",
        entityId: "about",
      }),
      pageShortcut({
        path: "services",
        name: "Services & Booking",
        icon: "CalendarMonth",
        description: "Every session card and every piece of text on the Services page.",
        collectionId: "page-services",
        entityId: "services",
      }),
      pageShortcut({
        path: "community",
        name: "Community & Stories",
        icon: "Favorite",
        description: "The wording around the client stories. The stories themselves live under Testimonials.",
        collectionId: "page-community",
        entityId: "community",
      }),
      pageShortcut({
        path: "contact",
        name: "Contact",
        icon: "Email",
        description: "Every piece of text and the photo on the Contact page.",
        collectionId: "page-contact",
        entityId: "contact",
      }),
    ],
    [],
  );

  const modeController = useBuildModeController();

  const signInOptions: FirebaseSignInProvider[] = ["google.com", "password"];

  const authController: FirebaseAuthController = useFirebaseAuthController({
    firebaseApp,
    signInOptions,
  });

  const userConfigPersistence = useBuildLocalConfigurationPersistence();

  const firestoreDelegate = useFirestoreDelegate({ firebaseApp });

  const storageSource = useFirebaseStorageSource({ firebaseApp });

  const { authLoading, canAccessMainView, notAllowedError } =
    useValidateAuthenticator({
      authController,
      authenticator: myAuthenticator,
      dataSourceDelegate: firestoreDelegate,
      storageSource,
    });

  const navigationController = useBuildNavigationController({
    disabled: authLoading,
    collections,
    views,
    authController,
    dataSourceDelegate: firestoreDelegate,
  });

  if (firebaseConfigLoading || !firebaseApp) {
    return <CircularProgressCenter />;
  }

  if (configError) {
    return <CenteredView>{configError}</CenteredView>;
  }

  return (
    <FireCMSi18nProvider>
      <SnackbarProvider>
        <ModeControllerProvider value={modeController}>
          <FireCMS
            navigationController={navigationController}
            authController={authController}
            userConfigPersistence={userConfigPersistence}
            dataSourceDelegate={firestoreDelegate}
            storageSource={storageSource}
          >
            {({ context, loading }) => {
              if (loading || authLoading) {
                return <CircularProgressCenter size={"large"} />;
              }

              if (!canAccessMainView) {
                return (
                  <FirebaseLoginView
                    authController={authController}
                    firebaseApp={firebaseApp}
                    signInOptions={signInOptions}
                    notAllowedError={notAllowedError}
                  />
                );
              }

              return (
                <Scaffold autoOpenDrawer={false}>
                  <AppBar title={"Rose Stroh — Content Manager"} />
                  <Drawer />
                  <NavigationRoutes />
                  <SideDialogs />
                </Scaffold>
              );
            }}
          </FireCMS>
        </ModeControllerProvider>
      </SnackbarProvider>
    </FireCMSi18nProvider>
  );
}

export default App;
